import * as pdfjsLib from 'pdfjs-dist';
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&inline';
import {useCallback, useState} from 'react';
import {useParserState} from './useParserState';

const PROGRESS_COMPLETE = 1;
const PROGRESS_INITIAL = 0;

let workerInitialized = false;

function ensureWorker(): void {
    if (!workerInitialized) {
        pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();
        workerInitialized = true;
    }
}

export interface UsePdfParserResult {
    parse: (file: File) => Promise<string[]>;
    loading: boolean;
    progress: number;
    error: Error | null;
}

export function usePdfParser(): UsePdfParserResult {
    const {loading, error, wrap} = useParserState();
    const [progress, setProgress] = useState(PROGRESS_INITIAL);

    const parse = useCallback(
        async (file: File): Promise<string[]> => {
            setProgress(PROGRESS_INITIAL);
            try {
                return await wrap(async () => {
                    ensureWorker();
                    const arrayBuffer = await file.arrayBuffer();
                    const pdf = await pdfjsLib.getDocument({data: arrayBuffer}).promise;
                    try {
                        let completedPages = 0;

                        return await Promise.all(
                            Array.from({length: pdf.numPages}, async (_, index) => {
                                const page = await pdf.getPage(index + 1);
                                const textContent = await page.getTextContent();

                                const text = textContent.items.map((item) => ('str' in item ? item.str : '')).join(' ');
                                page.cleanup();

                                completedPages += 1;
                                setProgress(completedPages / pdf.numPages);

                                return text;
                            })
                        );
                    } finally {
                        await pdf.destroy();
                    }
                });
            } finally {
                setProgress(PROGRESS_COMPLETE);
            }
        },
        [wrap]
    );

    return {parse, loading, progress, error};
}
